<template>
  <div
    class="flex h-full items-center justify-between px-8 py-4 sm:flex-row md:flex-col md:py-8"
  >
    <div class="flex items-center gap-10 sm:flex-row md:flex-col not-prose">
      <img
        src="../../assets/logo_small_colored.svg"
        style="width: 75px; height: 75px"
        class="my-2 max-h-10"
        alt="Logo"
        @click="goToHome()"
      />

      <component :is="routesComponent" />

      <router-link
        v-if="
          (currentRoute === routesNames.HOME ||
            currentRoute === routesNames.BUSINESS_ENTITIES_LIST) &&
          isDevelopmentEnvironment
        "
        data-testid="listTab"
        :to="{ name: 'BusinessEntitiesList' }"
      >
        <em
          :class="[
            'mdi mdi-list-box text-3xl hover:text-primary-2',
            {
              'text-primary-2':
                currentRoute === routesNames.BUSINESS_ENTITIES_LIST,
            },
          ]"
        ></em>
      </router-link>
    </div>

    <div class="flex items-center gap-5 sm:flex-row md:flex-col">
      <em
        v-if="
          isNative &&
          (isDmgw6Order || isDevelopmentEnvironment) &&
          currentRoute !== routesNames.HOME &&
          currentRoute !== routesNames.BUSINESS_ENTITIES_LIST &&
          !isInspectionOrder
        "
        class="mdi mdi-remote text-3xl"
        data-testid="sideNav_openQTool"
        :class="[
          {
            'text-green-500': isConnected,
            'text-red-500': isQtoolDisconnected,
          },
        ]"
        @click="goToDmgBoard"
      ></em>

      <em
        v-if="isSandboxMode"
        class="mdi text-3xl transition-all mdi-help-circle-outline"
        @click="openHelp"
      ></em>

      <em
        v-if="isDevelopmentEnvironment"
        :class="devIcon"
        class="mdi text-3xl transition-all"
        @dblclick="dblClick"
      ></em>

      <OptionsDropdown direction="up" data-testid="sideNav_optionsDropdown">
        <template #toggle="{ onClick }">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl bg-light-0 text-white shadow dark:bg-dark-0 hover:dark:bg-dark-4"
            @click="onClick"
          >
            <div
              class="no-selection text-xl font-bold text-black dark:text-white"
            >
              <em v-if="specialUserIcon" :class="specialUserIcon"></em>
              <div v-else>{{ firstLetter }}</div>
            </div>
          </div>
        </template>

        <li @click="goToOnboarding">
          <span v-if="isSandboxMode">
            <em class="mdi mdi-location-exit mr-3 leading-6" />
            Exit Training
          </span>
          <span v-else>
            <em class="mdi mdi-school-outline mr-3 leading-6" />
            Training
          </span>
        </li>

        <li class="flex flex-row" @click="toggleDarkMode">
          <div class="mr-3 flex h-5 w-4 flex-col overflow-hidden">
            <div
              class="transition-all duration-500"
              :class="{ 'rotate-180': useLayoutStore().isDarkMode() }"
            >
              <em
                class="mdi mdi-weather-night flex items-center justify-center leading-5"
              />
              <em
                class="mdi mdi-white-balance-sunny mt-5 flex items-center justify-center leading-5"
              />
            </div>
          </div>
          {{ useLayoutStore().isDarkMode() ? 'Light Mode' : 'Dark Mode' }}
        </li>

        <li v-if="isDevelopmentEnvironment" @click="goToDevSandbox">
          <em class="mdi mdi-abacus mr-3 leading-6" />
          Dev Sandbox
        </li>
        <li data-testid="openChangelog" @click="openChangelog">
          <em class="mdi mdi-text mr-3 leading-6" />
          Aktualisierungen
        </li>
        <li v-if="isNative" data-testid="openTutorial" @click="openTutorial">
          <em class="mdi mdi-school mr-3 leading-6" />
          Tutorial
        </li>
        <li data-testid="button-logout" @click="togglePrompt">
          <em class="mdi mdi-logout-variant mr-3 leading-6" />
          Logout
        </li>
        <div
          data-testid="version"
          class="px-5 pb-2 text-light-disabled-text dark:text-dark-disabled-text"
        >
          Version {{ appVersion }}
        </div>
      </OptionsDropdown>
    </div>
  </div>
  <BasePrompt
    :open="promptOpen"
    :close="togglePrompt"
    :cancel="togglePrompt"
    :proceed="logout"
    title="Logout"
    question="Sind Sie sicher, dass Sie sich ausloggen möchten?"
  />
  <AppChangelogModal
    v-if="showChangelogDialog"
    @close-dialog="closeChangelogDialog()"
  />
</template>

<script lang="ts" setup>
import { useUserStore } from '@/store/user/userStore';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import { default as envConfig } from '@/config/runtimeConfig';
import { useAppStore } from '@/store/app/AppStore';
import { Capacitor } from '@capacitor/core';
import { Order } from '@/models/Order';
import { routesNames } from '@/router';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { OnboardingDialog } from '@/native/plugins/OnboardingDialog';
import { WebEvents } from '@/native/plugins/WebEvents';
import { useOrderStore } from '@/store/order/orderStore';
import AppChangelogModal from '@/components/OrderManagement/AppChangelogModal.vue';
import { useHelp } from '@/composables/useHelp';
import { useQToolConnection } from '@/composables/useQToolConnection';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models/Notification';

const router = useRouter();

const props = defineProps<{
  selectedOrder?: Order;
  isServiceListOpen?: boolean;
}>();
const emit = defineEmits(['toggleServiceList']);
const { openHelp } = useHelp();

const firstLetter = computed(() => {
  const username = useUserStore().getUsername;
  return username ? username.charAt(0) : undefined;
});

const specialUserIcon = computed(() => {
  if (useUserStore().userAttributes?.picture) {
    return `mdi ${useUserStore().userAttributes?.picture}`;
  }
  return undefined;
});

const qtoolConnection = useQToolConnection();
const funkcheckConnected = ref(false);
const isConnected = computed(
  () => qtoolConnection.isConnected.value || funkcheckConnected.value
);
const isQtoolDisconnected = computed(() => {
  const phase = qtoolConnection.state.value.phase;
  return phase === 'disconnected' || phase === 'error';
});

watch(qtoolConnection.state, (newState, oldState) => {
  if (
    newState.phase === 'disconnected' &&
    oldState.phase !== 'idle' &&
    oldState.phase !== 'disconnected'
  ) {
    useNotificationStore().addNotification(
      new Notification()
        .setTitle('Q-Tool getrennt')
        .setText('Die Verbindung zum Q-Tool wurde unterbrochen.')
        .setType(Notification.WARNING)
    );
  }
});

const isInspectionOrder = computed(() => {
  return useOrderStore().isOnSiteInspection();
});

// The board is Q-Tool tooling for the DMGW6 install flow. On an order without
// the DMGW6 article the installer follows the legacy funkcheck wizard and there
// is no board to pair with, so the entry point must not be offered — except in
// a development build, where the board is also how the Q-Tool firmware update
// is exercised (its debug trigger lives there) and that has to be possible on
// whatever order is at hand.
const isDmgw6Order = computed(() => {
  return useOrderStore().activeOrder?.hasDmgw6Article() ?? false;
});

const appVersion = computed(() => {
  return APP_VERSION;
});

const goToDmgBoard = () => {
  router.push({ name: routesNames.DMGW_BOARD });
};

const openTutorial = () => {
  return OnboardingDialog.openDialog();
};

const openChangelog = () => {
  return useAppStore().toggleChangelogDialog();
};

const isNative = computed(() => {
  return Capacitor.isNativePlatform();
});

const isDevelopmentEnvironment = computed(() => {
  return envConfig.isDevelopment === 'true';
});

const isSandboxMode = computed(() => {
  return useLayoutStore().isSandboxMode;
});

const devIcon = ref('mdi-robot-outline text-primary-2');
const dblClicked = ref(0);
const dblClick = () => {
  dblClicked.value++;
  if (dblClicked.value === 3) {
    devIcon.value = 'mdi-robot-angry-outline animate-wiggle text-red-500';
    dblClicked.value = 0;
    setTimeout(() => {
      devIcon.value = 'mdi-robot-outline text-primary-2';
    }, 3000);
    return;
  }
  devIcon.value =
    'mdi-robot-happy-outline animate-[bounce_0.5s_ease-out_infinite] text-green-500';
  setTimeout(() => {
    devIcon.value = 'mdi-robot-outline text-primary-2 ';
  }, 2750);
};

const logout = () => {
  useUserStore()
    .logout()
    .then(() => {
      router.push({ name: 'Login' });
    });
};
const toggleDarkMode = () => {
  const layoutStore = useLayoutStore();
  if (layoutStore.isDarkMode()) {
    layoutStore.deactivateDarkMode();
  } else {
    layoutStore.activateDarkMode();
  }
};

const currentRoute = computed(() => {
  return router.currentRoute.value.name;
});

const goToUpdaterPage = () => {
  router.push({ path: '/updater' });
};

const goToDevSandbox = () => {
  router.push({ path: '/devMode' });
};

const goToOnboarding = () => {
  if (isSandboxMode.value) {
    window.location.href = '/';
    return;
  }
  window.location.href = '/sandboxMode';
};

const goToHome = () => {
  if (useLayoutStore().isSandboxMode) {
    router.push({ path: '/sandboxMode' });
    return;
  }

  /*
   * If the user is not in order mode we force refresh the home page
   * So local state is NOT preserved
   */
  if (useLayoutStore().workMode !== WorkMode.ORDER) {
    window.location.replace('/');
    return;
  }
  router.push({ path: '/' });
};

const promptOpen = ref(false);
const togglePrompt = () => {
  promptOpen.value = !promptOpen.value;
};

const inspectionRoutes = defineAsyncComponent(
  () => import('@/components/SideMenu/Navigation/InspectionNav.vue')
);
const installationRoutes = defineAsyncComponent(
  () => import('@/components/SideMenu/Navigation/InstallationNav.vue')
);

const routesComponent = computed(() => {
  if (useOrderStore().isOnSiteInspection()) {
    return inspectionRoutes;
  }
  return installationRoutes;
});

const appStore = useAppStore();
const showChangelogDialog = computed(() => appStore.isChangelogOpen);
const closeChangelogDialog = () => {
  appStore.closeChangelogDialog();
};
</script>
